import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, Alert } from 'react-native';
import { Card, Button, TextInput, RadioButton, ActivityIndicator } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import api from '../../services/api';

const NuevaPenalizacionScreen = () => {
  const navigation = useNavigation();
  const [usuarios, setUsuarios] = useState([]);
  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    usuario_id: '',
    tipo: 'penalizacion',
    motivo: '',
    monto: '',
    notas: '',
    regla_id: '',
    venta_id: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usuariosData, reglasData] = await Promise.all([
        api.getUsuarios(),
        api.getPenalizacionReglas().catch(() => []),
      ]);
      setUsuarios(Array.isArray(usuariosData) ? usuariosData : []);
      setReglas(Array.isArray(reglasData) ? reglasData : []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleReglaSelect = (reglaId) => {
    const regla = reglas.find(r => r.id === parseInt(reglaId));
    if (regla) {
      setForm({
        ...form,
        regla_id: reglaId,
        tipo: regla.tipo,
        motivo: regla.motivo,
        monto: regla.monto.toString(),
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.usuario_id) newErrors.usuario_id = 'Seleccione un usuario';
    if (!form.motivo.trim()) newErrors.motivo = 'Ingrese el motivo';
    if (!form.monto || parseFloat(form.monto) <= 0) newErrors.monto = 'Ingrese un monto válido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSaving(true);
      await api.createPenalizacion({
        usuario_id: parseInt(form.usuario_id),
        regla_id: form.regla_id ? parseInt(form.regla_id) : null,
        venta_id: form.venta_id ? parseInt(form.venta_id) : null,
        tipo: form.tipo,
        motivo: form.motivo,
        monto: parseFloat(form.monto),
        notas: form.notas || null,
      });

      Alert.alert(
        form.tipo === 'bono' ? 'Bono Aplicado' : 'Penalización Aplicada',
        `${form.tipo === 'bono' ? 'Bono' : 'Penalización'} de L. ${parseFloat(form.monto).toFixed(2)} registrada`
      );
      navigation.goBack();
    } catch (error) {
      Alert.alert('Error', error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      <Card style={styles.card}>
        <Card.Title title="Tipo" />
        <Card.Content>
          <RadioButton.Group onValueChange={v => handleChange('tipo', v)} value={form.tipo}>
            <RadioButton.Item label="Penalización" value="penalizacion" />
            <RadioButton.Item label="Bono" value="bono" />
          </RadioButton.Group>
        </Card.Content>
      </Card>

      {/* Regla predefinida */}
      {reglas.filter(r => r.tipo === form.tipo && r.activo).length > 0 && (
        <Card style={styles.card}>
          <Card.Title title="Usar regla predefinida (opcional)" />
          <Card.Content>
            <RadioButton.Group onValueChange={handleReglaSelect} value={form.regla_id}>
              <RadioButton.Item label="Sin regla (manual)" value="" />
              {reglas.filter(r => r.tipo === form.tipo && r.activo).map(r => (
                <RadioButton.Item
                  key={r.id}
                  label={`${r.nombre} - L. ${parseFloat(r.monto).toFixed(2)}`}
                  value={r.id.toString()}
                />
              ))}
            </RadioButton.Group>
          </Card.Content>
        </Card>
      )}

      <Card style={styles.card}>
        <Card.Title title="Detalles" />
        <Card.Content>
          <TextInput
            label="ID Usuario*"
            value={form.usuario_id}
            onChangeText={v => handleChange('usuario_id', v)}
            mode="outlined"
            keyboardType="numeric"
            error={!!errors.usuario_id}
            style={styles.input}
            right={<TextInput.Affix text={usuarios.find(u => u.id === parseInt(form.usuario_id))?.nombre || ''} />}
          />
          {errors.usuario_id && <Text style={styles.errorText}>{errors.usuario_id}</Text>}

          <TextInput
            label="Motivo*"
            value={form.motivo}
            onChangeText={v => handleChange('motivo', v)}
            mode="outlined"
            error={!!errors.motivo}
            style={styles.input}
          />
          {errors.motivo && <Text style={styles.errorText}>{errors.motivo}</Text>}

          <TextInput
            label="Monto (L.)*"
            value={form.monto}
            onChangeText={v => handleChange('monto', v)}
            mode="outlined"
            keyboardType="decimal-pad"
            error={!!errors.monto}
            style={styles.input}
          />
          {errors.monto && <Text style={styles.errorText}>{errors.monto}</Text>}

          <TextInput
            label="Venta # (opcional)"
            value={form.venta_id}
            onChangeText={v => handleChange('venta_id', v)}
            mode="outlined"
            keyboardType="numeric"
            style={styles.input}
          />

          <TextInput
            label="Notas (opcional)"
            value={form.notas}
            onChangeText={v => handleChange('notas', v)}
            mode="outlined"
            multiline
            numberOfLines={3}
            style={styles.input}
          />

          <Button
            mode="contained"
            onPress={handleSubmit}
            loading={saving}
            disabled={saving}
            style={[styles.submitButton, { backgroundColor: form.tipo === 'bono' ? '#28A745' : '#DC3545' }]}
            icon={form.tipo === 'bono' ? 'plus-circle' : 'minus-circle'}
          >
            {form.tipo === 'bono' ? 'Aplicar Bono' : 'Aplicar Penalización'}
          </Button>
        </Card.Content>
      </Card>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  scrollContent: { paddingBottom: 20 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  card: { margin: 8, elevation: 2 },
  input: { marginBottom: 8 },
  errorText: { color: '#DC3545', fontSize: 12, marginBottom: 8, marginLeft: 4 },
  submitButton: { marginTop: 12 },
});

export default NuevaPenalizacionScreen;
